import * as Yup from "yup";

import { DealMetricType } from "@vega-api/generated/types/dealMetricType";
import { m } from "@vega-core/paraglide/messages";

export const getCoInvestmentValidationSchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .required(m.coInvestments_create_validation_nameRequired())
      .matches(/\S/, m.coInvestments_create_validation_nameRequired()),
    fundName: Yup.string()
      .required(m.coInvestments_create_validation_fundRequired())
      .matches(/\S/, m.coInvestments_create_validation_fundRequired()),
  });

const valueRequiredMessage =
  m.coInvestments_lpDealOverviewContent_valueRequired;

const metricValueSchema = Yup.mixed().when("type", {
  is: DealMetricType.MONEY,
  then: () =>
    Yup.object({
      amount: Yup.string().required(valueRequiredMessage()),
    }),
  otherwise: () => Yup.string().required(valueRequiredMessage()),
});

export const getCoInvestmentDetailsValidationSchema = () =>
  Yup.object()
    .shape({
      name: Yup.string().required(
        m.formValidation_required({ name: m.common_name() }),
      ),
      currency: Yup.string().required(
        m.formValidation_required({ name: m.common_currency() }),
      ),
      description: Yup.string().nullable().optional(),
      closingDate: Yup.string().nullable().optional(),
      targetAmount: Yup.object({
        amount: Yup.number().nullable(),
        currency: Yup.string(),
      })
        .nullable()
        .optional(),
      totalDealSize: Yup.object({
        amount: Yup.number().nullable(),
        currency: Yup.string(),
      })
        .nullable()
        .optional(),
      fundContributionAmount: Yup.object({
        amount: Yup.number().nullable(),
        currency: Yup.string(),
      })
        .nullable()
        .optional(),

      // Teaser Information (optional)
      teaserFiles: Yup.array().of(Yup.mixed()).nullable(),

      // Deal Overview (required fields)
      dealOverview: Yup.object({
        metrics: Yup.array()
          .of(
            Yup.object({
              id: Yup.string(),
              name: Yup.string().required(
                m.formValidation_required({
                  name: m.coInvestments_lpDealOverviewContent_label(),
                }),
              ),
              value: metricValueSchema,
              type: Yup.string().required(),
            }),
          )
          .optional(),
        sections: Yup.array()
          .of(
            Yup.object({
              id: Yup.string(),
              name: Yup.string().required(
                m.formValidation_required({
                  name: m.coInvestments_lpDealOverviewContent_label(),
                }),
              ),
              content: Yup.string().required(
                m.formValidation_required({
                  name: m.coInvestments_lpDealOverviewContent_content(),
                }),
              ),
            }),
          )
          .optional(),
      }).optional(),
    })
    .test({
      name: "fund-contribution-within-deal-size",
      test: (values, context) => {
        // A cleared MoneyField arrives here as undefined — Formik strips the
        // empty string before validation — so treat a missing amount as 0,
        // consistent with what is persisted on save (VD-28160). Clearing the
        // total deal size while a contribution remains then exceeds it.
        const contributionAmount = values.fundContributionAmount?.amount ?? 0;
        const totalDealSizeAmount = values.totalDealSize?.amount ?? 0;

        if (contributionAmount > totalDealSizeAmount) {
          return context.createError({
            path: "fundContributionAmount.amount",
            message:
              m.coInvestments_editDealForm_validation_contributionExceedsDealSize(),
          });
        }

        return true;
      },
    });
